import { useTheme } from '@mui/material/styles'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import useAuth from '@/hooks/useAuth'
import { canSeeCost } from '@/lib/costVisibility'
import { buildTourSteps, TOUR_READY_SELECTOR, TOUR_START_PATH } from './steps'
import { canOpenTourRoute, filterTourSteps } from './tourAccess'
import { waitForElement } from './waitForElement'

const START_WAIT_MS = 5000

// If storage throws (private window, blocked site data), the tour must still
// auto-start only once per page load rather than on every visit to Overview.
const seenThisSession = new Set()

export const tourSeenKey = (orgId, userId) =>
    `demo_tour_seen:${orgId}:${userId ?? 'anon'}`

function hasSeenTour(key) {
    if (seenThisSession.has(key)) return true
    try {
        return window.localStorage.getItem(key) !== null
    } catch {
        return false
    }
}

function markTourSeen(key) {
    seenThisSession.add(key)
    try {
        window.localStorage.setItem(key, new Date().toISOString())
    } catch {
        // Storage unavailable: the in-memory set above covers this session.
    }
}

/**
 * The demo-school product tour: auto-starts once on Power Overview for a
 * demo org (`activeOrg.is_demo`), and `startTour()` replays it from step 1.
 *
 * Lives in the top bar, which stays mounted across dashboard pages, so the
 * tour survives its own route changes; unmounting or switching org tears it
 * down so no overlay is left behind.
 */
export default function useDemoTour() {
    const { isAuthenticated, user, activeOrg } = useAuth()
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const theme = useTheme()

    const pathnameRef = useRef(pathname)
    pathnameRef.current = pathname
    const runnerRef = useRef(null)
    // Bumped to cancel a launch still waiting for the page to render.
    const launchRef = useRef(0)

    const orgId = activeOrg?.id
    const seenKey = tourSeenKey(orgId, user?.id)

    const access = useMemo(
        () => ({
            isAuthenticated,
            isStaff: user?.isStaff === true,
            user,
            activeOrg
        }),
        [isAuthenticated, user, activeOrg]
    )

    const canTour =
        activeOrg?.is_demo === true &&
        Boolean(orgId) &&
        canOpenTourRoute('power-overview', access)

    const steps = useMemo(
        () =>
            canTour
                ? filterTourSteps(
                      buildTourSteps({
                          orgName: activeOrg?.name,
                          showCost: canSeeCost(user, activeOrg)
                      }),
                      access
                  )
                : [],
        [canTour, access, activeOrg, user]
    )

    const cssVars = useMemo(
        () => ({
            '--tour-bg': theme.palette.background.paper,
            '--tour-text': theme.palette.text.primary,
            '--tour-muted': theme.palette.text.secondary,
            '--tour-primary': theme.palette.primary.main,
            '--tour-primary-contrast': theme.palette.primary.contrastText,
            '--tour-border': theme.palette.divider,
            '--tour-font': theme.typography.fontFamily,
            '--tour-shadow': theme.shadows[16]
        }),
        [theme]
    )

    const launch = useCallback(
        async ({ requireAnchor }) => {
            runnerRef.current?.destroy()
            runnerRef.current = null
            launchRef.current += 1
            const launchId = launchRef.current
            const stale = () => launchId !== launchRef.current

            if (pathnameRef.current !== TOUR_START_PATH) {
                navigate(TOUR_START_PATH)
            }
            // Open the welcome once Overview's data is on screen, so Next
            // lands on a rendered page rather than a skeleton.
            const el = await waitForElement(
                TOUR_READY_SELECTOR,
                START_WAIT_MS,
                stale
            )
            if (stale() || (requireAnchor && !el)) return

            const { createTourRunner } = await import('./tourRunner')
            if (stale()) return

            const runner = createTourRunner({
                steps,
                navigate,
                getPathname: () => pathnameRef.current,
                cssVars,
                onFinish: () => {
                    markTourSeen(seenKey)
                    if (runnerRef.current === runner) runnerRef.current = null
                }
            })
            runnerRef.current = runner
            runner.start()
        },
        [steps, navigate, cssVars, seenKey]
    )

    // Auto-start once per org and user, on Overview only.
    useEffect(() => {
        if (
            !canTour ||
            pathname !== TOUR_START_PATH ||
            runnerRef.current ||
            hasSeenTour(seenKey)
        ) {
            return undefined
        }
        launch({ requireAnchor: true })
        return () => {
            launchRef.current += 1
        }
    }, [canTour, pathname, seenKey, launch])

    // A route change the tour didn't make (browser back) ends it.
    useEffect(() => {
        runnerRef.current?.handleLocationChange(pathname)
    }, [pathname])

    // Switching org or leaving the dashboard layout: no overlay survives.
    useEffect(
        () => () => {
            launchRef.current += 1
            runnerRef.current?.destroy()
            runnerRef.current = null
        },
        [orgId]
    )

    const startTour = useCallback(
        () => launch({ requireAnchor: false }),
        [launch]
    )

    return { canTour, startTour }
}
